// Paint layer for the room-mode calculator's stem plot — axial/tangential/
// oblique stems, the Schroeder band, cluster warnings. Geometry (log-freq
// positions, ticks) comes from `computeModeStemGeometry` in
// @roomtreatment/charts; per-mode colour/height and cluster-finding stay in
// @/lib/tools/modeSpectrum.ts (product logic, not layout math). This file
// owns only the ctx calls, per AGENTS.md's chart/plot rule.
//
// The bespoke version of this chart hand-rolled its own hover model (a
// positioned tooltip <div> from local onMouseMove state) that redrew the
// WHOLE canvas on every pointer move to grow the nearest stem. Going through
// Chart.tsx drops that per-mousemove canvas redraw entirely: the stems paint
// once, and hover/selection are the one shared crosshair + standing-marker
// overlay every other chart in the app uses (see `plot.x.format` below for
// how the crosshair still names the mode, its indices and its frequency).
import { canvasTypeFont, withAlpha } from '@roomtreatment/design';
import { computeModeStemGeometry, formatFreqReadout, type ChartPad } from '@roomtreatment/charts';
import type { RoomMode } from '@/lib/acousticsPhysics';
import { findModeClusters, modeStemColor, modeStemHeight, nearestMode } from '@/lib/tools/modeSpectrum';
import { tokensAt } from '@/lib/theme/tokens';
import { canvasInkAt } from '@/components/room/canvasInk';
import {
  setupChartCanvas, EMPTY_PAINT, X_TICK_LABEL_Y_OFFSET, type ChartPaint,
} from '@/components/room/charts/chartCanvas';

export const PAD_MODE_STEM: ChartPad = { t: 22, r: 14, b: 26, l: 10 };

export interface ModeSpectrumChartOpts {
  modes: RoomMode[];
  schroederHz: number;
  fMin?: number;
  fMax?: number;
  /** Dim modes not matching this type. */
  filterType: 'all' | RoomMode['type'];
  /** Highlight a selected mode frequency (the standing accent marker). */
  selectedHz?: number | null;
}

export function drawModeSpectrum(canvas: HTMLCanvasElement, chart: ModeSpectrumChartOpts): ChartPaint {
  const s = setupChartCanvas(canvas, 148);
  if (!s) return EMPTY_PAINT;
  const { ctx, W, H } = s;

  const fMin = chart.fMin ?? 20;
  const fMax = chart.fMax ?? 250;
  const items = chart.modes.filter((m) => m.freqHz >= fMin && m.freqHz <= fMax);
  const clusters = findModeClusters(items, 5);

  const geo = computeModeStemGeometry({
    modes: items.map((m) => ({ freqHz: m.freqHz })),
    schroederHz: chart.schroederHz,
    fMin,
    fMax,
    width: W,
    height: H,
    pad: PAD_MODE_STEM,
    measureWidth: (label) => ctx.measureText(label).width,
  });
  if (geo.empty) return EMPTY_PAINT;
  const { frame } = geo;
  const t = tokensAt(canvas);

  // `computeModeStemGeometry` filters to the SAME [fMin, fMax] `items`
  // already is, so every entry survives in order — stems[i] is items[i].
  const stemX = geo.stems.map((p) => p.x);

  // Modal region (below Schroeder).
  ctx.fillStyle = withAlpha(t.accent, 0.06);
  ctx.fillRect(frame.plotX, frame.plotY, Math.max(0, geo.schroederX), frame.plotH);

  // Cluster bands.
  for (const c of clusters) {
    const idxs = c.modes.map((m) => items.indexOf(m)).filter((i) => i >= 0);
    if (!idxs.length) continue;
    const xs = idxs.map((i) => stemX[i]!);
    const left = frame.plotX + Math.min(...xs) - 4;
    const right = frame.plotX + Math.max(...xs) + 4;
    ctx.fillStyle = withAlpha(t.danger, 0.1);
    ctx.fillRect(left, frame.plotY, right - left, frame.plotH);
    ctx.strokeStyle = withAlpha(t.danger, 0.32);
    ctx.lineWidth = 1;
    ctx.strokeRect(left, frame.plotY + 1, right - left, frame.plotH - 2);
  }

  // Grid: x (log-freq) ticks only — stem height is a fixed weight per mode
  // type, not a data value, so there is no y axis to draw.
  // Tick numbers in the same role and ink as every other chart's axis.
  ctx.strokeStyle = withAlpha(t.ink, 0.1);
  ctx.lineWidth = 1;
  ctx.textAlign = 'center';
  ctx.fillStyle = canvasInkAt(canvas).label;
  ctx.font = canvasTypeFont('tick');
  for (const tick of geo.xTicks.labeled) {
    const x = frame.plotX + tick.x;
    ctx.beginPath();
    ctx.moveTo(x, frame.plotY);
    ctx.lineTo(x, frame.plotY + frame.plotH);
    ctx.stroke();
    ctx.fillText(tick.label, x, frame.plotY + frame.plotH + X_TICK_LABEL_Y_OFFSET);
  }

  // Baseline.
  ctx.strokeStyle = withAlpha(t.ink, 0.18);
  ctx.beginPath();
  ctx.moveTo(frame.plotX, frame.plotY + frame.plotH);
  ctx.lineTo(frame.plotX + frame.plotW, frame.plotY + frame.plotH);
  ctx.stroke();

  // Stems — muted first, active on top (axial drawn last via sort).
  const order = items.map((_, i) => i).sort((a, b) => {
    const rank = (ty: RoomMode['type']) => (ty === 'oblique' ? 0 : ty === 'tangential' ? 1 : 2);
    return rank(items[a]!.type) - rank(items[b]!.type);
  });

  for (const i of order) {
    const m = items[i]!;
    const muted = chart.filterType !== 'all' && m.type !== chart.filterType;
    const selected = chart.selectedHz != null && Math.abs(m.freqHz - chart.selectedHz) < 0.05;
    const x = frame.plotX + stemX[i]!;
    const h = modeStemHeight(m.type) * frame.plotH * (selected ? 1 : 0.92);
    const y0 = frame.plotY + frame.plotH;
    const y1 = y0 - h;

    ctx.strokeStyle = modeStemColor(m.type, muted && !selected, t);
    ctx.lineWidth = selected ? 3 : muted ? 1.25 : 2;
    ctx.globalAlpha = muted && !selected ? 0.55 : 1;
    ctx.beginPath();
    ctx.moveTo(x, y0);
    ctx.lineTo(x, y1);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(x, y1, selected ? 3.5 : 2.5, 0, Math.PI * 2);
    ctx.fillStyle = modeStemColor(m.type, muted && !selected, t);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // Schroeder line + label.
  const fsX = frame.plotX + geo.schroederX;
  ctx.strokeStyle = withAlpha(t.accent, 0.85);
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.moveTo(fsX, frame.plotY);
  ctx.lineTo(fsX, frame.plotY + frame.plotH);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = t.accent;
  ctx.font = canvasTypeFont('label');
  ctx.textAlign = 'left';
  const fsLabel = `f_s ${chart.schroederHz}`;
  const labelW = ctx.measureText(fsLabel).width;
  ctx.fillText(fsLabel, Math.min(fsX + 4, W - labelW - 8), frame.plotY + 12);

  if (clusters.length > 0) {
    ctx.fillStyle = withAlpha(t.danger, 0.9);
    ctx.font = canvasTypeFont('label');
    ctx.textAlign = 'left';
    ctx.fillText('dense clusters', frame.plotX + 4, frame.plotY + 12);
  }

  return {
    caption: '',
    plot: {
      frame,
      x: {
        scale: geo.xScale,
        // The crosshair names the mode under the pointer — its indices
        // (the (nx,ny,nz) label), its type and its frequency — rather than
        // just the raw Hz value: this is the one piece of information the
        // old hand-rolled tooltip carried that a plain frequency readout
        // would have lost. See nearestMode's own doc for why there is no
        // proximity cutoff.
        format: (v: number) => {
          const m = nearestMode(items, v);
          return m ? `${m.label} ${m.type} · ${formatFreqReadout(m.freqHz)} Hz` : `${formatFreqReadout(v)} Hz`;
        },
      },
    },
  };
}
